<template>
    <Drawer hashKey="player-selection" :autoHeight="true">
        <div class="flex flex-1 flex-col overflow-hidden">
            <div class="flex items-center gap-1 mb-4">
                <span class="text-titles text-xs font-semibold uppercase">{{ $t('player_selector.party_size') }}: </span>
                <span class="text-sm font-bold text-white">{{ partySize.count }}/{{ partySize.cap }}</span>
                <button
                    type="button"
                    @click="closeDrawer"
                    :aria-label="$t('general.close')"
                    class="ml-auto flex items-center"
                >
                    <svg viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-3">
                        <path d="M1.41406 -6.18106e-08L6.70703 5.29297L12 -5.24537e-07L13.4141 1.41406L6.70703 8.12109L-2.93174e-07 1.41406L1.41406 -6.18106e-08Z" fill="white"/>
                    </svg>
                </button>
            </div>

            <TabBar v-model="activeTab" :tabs="tabs" />

            <div class="flex-1 overflow-y-auto py-4">
                <div v-if="activeTab === 'friends'" class="relative space-y-4">
                    <p class="text-lg font-extrabold text-white uppercase">{{ $t('player_selector.play_with_friends') }}</p>

                    <div class="space-y-3">
                        <div>
                            <FriendSearchInput
                                v-model="friendSearchQuery"
                                :placeholder="$t('friends.search_placeholder')"
                            />
                        </div>

                        <template v-if="friendSearchQuery.trim()">
                            <div class="space-y-2">
                                <div
                                    v-if="filteredFriends.length === 0"
                                    class="pb-4 font-semibold text-white"
                                >
                                    {{ $t('player_selector.no_friend_found') }}
                                </div>
                                <FriendItem
                                    v-for="friendship in filteredFriends"
                                    :key="friendship.id"
                                    :friend="friendship.friend"
                                    :show-online-indicator="true"
                                    :is-online="isOnline(friendship.friend.last_seen_at)"
                                >
                                    <template #button>
                                        <span
                                            v-if="friendMatchStatus(friendship.friend.id)"
                                            class="bg-zinc-900 shrink-0 border border-zinc-700 px-3 py-1.5 text-xs font-bold uppercase"
                                            :class="friendMatchStatus(friendship.friend.id) === 'joined' ? 'text-green-500' : 'text-orange-500'"
                                        >
                                            {{ friendStatusBadge(friendship.friend.id) }}
                                        </span>
                                        <DiagonalCutButton
                                            v-else
                                            variant="primary"
                                            cut-position="left-top"
                                            :full-width="false"
                                            :loading="loadingFriendId === friendship.friend.id"
                                            :disabled="loadingFriendId === friendship.friend.id"
                                            data-testid="friend-item-action-button"
                                            @click="sendGameInviteToFriend(friendship.friend)"
                                        >
                                            {{ loadingFriendId === friendship.friend.id ? $t('player_selector.adding') : $t('player_selector.add') }}
                                        </DiagonalCutButton>
                                    </template>
                                </FriendItem>
                            </div>

                            <div class="space-y-2">
                                <div
                                    v-if="searchResults.length === 0 && !isSearching"
                                    class="pb-4 font-semibold text-white"
                                >
                                    {{ $t('player_selector.no_user_found') }}
                                </div>

                                <div v-if="isSearching" class="text-titles py-4 text-center">
                                    {{ $t('player_selector.searching') }}
                                </div>

                                <div v-if="searchResults.length > 0">
                                    <Panel
                                        v-for="friend in searchResults"
                                        :key="friend.id"
                                        class="w-full text-white"
                                    >
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center space-x-3">
                                                <div>
                                                    <Avatar :src="friend.avatar" :size="10" />
                                                </div>
                                                <div>
                                                    <p class="text-sm font-semibold text-white">
                                                        {{ friend.tag_name }}
                                                    </p>
                                                    <p class="text-titles text-sm">{{ friend.name }}</p>
                                                </div>
                                            </div>
                                            <button
                                                v-if="!friendMatchStatus(friend.id)"
                                                @click="sendGameInviteToFriend(friend)"
                                                :disabled="isUpdating"
                                                class="rounded-full p-2 text-red-500 transition-colors hover:bg-green-50 hover:text-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                :title="$t('player_selector.add_to_match')"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke-width="2"
                                                    stroke="currentColor"
                                                    class="size-5"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        d="M12 4.5v15m7.5-7.5h-15"
                                                    />
                                                </svg>
                                            </button>
                                            <span
                                                v-else
                                                class="bg-zinc-900 shrink-0 border border-zinc-700 px-3 py-1.5 text-xs font-bold uppercase"
                                                :class="friendMatchStatus(friend.id) === 'joined' ? 'text-green-500' : 'text-orange-500'"
                                            >
                                                {{ friendStatusBadge(friend.id) }}
                                            </span>
                                        </div>
                                    </Panel>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <AccordionSection
                                v-if="onlineFriends.length > 0"
                                :title="$t('profile.online')"
                                :count="onlineFriends.length"
                                v-model="showOnlineSection"
                            >
                                <PlayersList
                                    :players="onlineFriends"
                                    :loading-player="loadingFriendId"
                                    :match-profiles-by-friend-id="matchProfilesByFriendId"
                                    @action="sendGameInviteToFriend"
                                />
                            </AccordionSection>

                            <AccordionSection
                                v-if="offlineFriends.length > 0"
                                :title="$t('profile.offline')"
                                :count="offlineFriends.length"
                                v-model="showOfflineSection"
                            >
                                <PlayersList
                                    :players="offlineFriends"
                                    :loading-player="loadingFriendId"
                                    :match-profiles-by-friend-id="matchProfilesByFriendId"
                                    @action="sendGameInviteToFriend"
                                />

                            </AccordionSection>

                            <div
                                v-if="onlineFriends.length === 0 && offlineFriends.length === 0"
                                class="text-titles pb-2 text-sm"
                            >
                                {{ $t('player_selector.start_typing_to_search') }}
                            </div>
                        </template>

                        <!-- <button
                            @click="sendGameInvite"
                            :disabled="isUpdating"
                            class="bg-bg flex w-full"
                        >
                            <span class="inline-block bg-red-500 py-4">
                                <svg
                                    class="size-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M21.7225 2.27734L14.058 22.4882L9.87585 14.125L1.51172 9.9429L21.7225 2.27734ZM11.8379 13.5769L13.8059 17.5125L18.2767 5.72328L6.48748 10.1947L10.4236 12.1628L13.7753 8.81038L15.1896 10.2244L11.8379 13.5769Z"
                                        fill="white"
                                    />
                                </svg>
                            </span>
                            <span
                                class="border-stroke-1 flex flex-1 items-center border border-l-0 px-5 font-bold text-white"
                                >Share game invite
                                <svg
                                    width="9"
                                    height="14"
                                    viewBox="0 0 9 14"
                                    fill="none"
                                    class="ml-auto"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                                        fill="#797979"
                                    />
                                </svg>
                            </span>
                        </button>

                        <button @click="scanQRCode" :disabled="isUpdating" class="bg-bg flex w-full">
                            <span class="inline-block bg-red-500 py-4">
                                <svg
                                    class="size-5"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 11H14M16 17H17M11 14V17H13M14 14H17V12M1 1H7V7H1V1ZM1 11H7V17H1V11ZM11 1H17V7H11V1Z"
                                        stroke="white"
                                        stroke-width="2"
                                    />
                                </svg>
                            </span>
                            <span
                                class="border-stroke-1 flex flex-1 items-center border border-l-0 px-5 font-bold text-white"
                                >Scan the QR-code
                                <svg
                                    width="9"
                                    height="14"
                                    viewBox="0 0 9 14"
                                    fill="none"
                                    class="ml-auto"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                                        fill="#797979"
                                    />
                                </svg>
                            </span>
                        </button> -->
                    </div>
                    <!-- <div class="absolute w-full h-full bg-bg/80 top-0 left-0" v-if="maxPlayersReached"></div> -->
                </div>

                <div v-if="activeTab === 'guest'" class="space-y-4">
                    <p class="text-lg font-extrabold text-white uppercase">{{ $t('player_selector.add_guest_heading') }}</p>

                    <div class="space-y-3">
                        <div>
                            <input
                                v-model="guestName"
                                type="text"
                                :placeholder="$t('player_selector.guest_placeholder')"
                                data-testid="guest-name-input"
                                class="border-stroke-1 bg-card w-full border p-3 focus:border-red-500 focus:ring-2 focus:ring-red-500"
                                maxlength="16"
                                @keyup.enter="addGuest"
                            />
                        </div>

                        <DiagonalCutButton
                            @click="addGuest"
                            :disabled="!guestName.trim() || isUpdating"
                            variant="primary"
                            cut-position="left-top"
                            data-testid="add-guest-button"
                        >
                            <span v-if="isUpdating">{{ $t('player_selector.adding') }}</span>
                            <span v-else>{{ $t('player_selector.add') }}</span>
                        </DiagonalCutButton>
                    </div>
                </div>
            </div>
        </div>

        <shareQr
            v-if="shareQr"
            :model-value="shareQr"
            :url="shareQrUrl"
            @close="closeQrModal"
            @update:model-value="shareQr = $event"
        />
    </Drawer>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import { useFriends } from '@/composables/useFriends.js'
import { useTeamManagement } from '@/composables/useTeamManagement.js'
import { usePlayerValidation } from '@/composables/usePlayerValidation.js'
import { isOnline } from '@/utils/presence.js'
import FriendSearchInput from '@/components/ui/FriendSearchInput.vue'
import FriendItem from '@/components/ui/FriendItem.vue'
import ShareQr from '@/components/ui/ShareQr.vue'
import Drawer from '@/components/ui/Drawer.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import AccordionSection from '@/components/ui/AccordionSection.vue'
import TabBar from '@/components/ui/TabBar.vue'
import PlayersList from './PlayersList.vue'

export default {
    name: 'PlayerSelectionDrawer',
    components: {
        FriendSearchInput,
        FriendItem,
        ShareQr,
        Drawer,
        DiagonalCutButton,
        PlayersList,
        AccordionSection,
        TabBar,
    },
    setup() {
        const friendsComposable = useFriends()
        // checkPlayerLimits needs the shared player-limit rules. Picked
        // explicitly so the composable's maxPlayersReached doesn't shadow
        // the prop of the same name.
        const playerValidation = usePlayerValidation(useTeamManagement())

        return {
            ...friendsComposable,
            requiresTeamModeForMorePlayers: playerValidation.requiresTeamModeForMorePlayers,
            getTeamModeRequiredMessage: playerValidation.getTeamModeRequiredMessage,
            getMaxPlayersMessage: playerValidation.getMaxPlayersMessage,
            effectiveMaxPlayers: playerValidation.effectiveMaxPlayers,
            isOnline,
        }
    },
    data() {
        return {
            activeTab: useMatchStore().isRemotePlay ? 'friends' : 'guest',
            shareQr: false,
            shareQrUrl: '',
            guestName: '',
            isUpdating: false,
            showOnlineSection: true,
            showOfflineSection: true,
        }
    },
    props: {
        maxPlayersReached: {
            type: Boolean,
            required: true,
        },
    },
    watch: {
        maxPlayersReached(newVal) {
            if (newVal && this.$router.currentRoute.value.hash === '#player-selection') {
                this.$router.replace({ hash: '' })
            }
        },
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        /** Online matches are friends-only: no guest (or bot) players. */
        tabs() {
            const tabs = [
                {
                    id: 'friends',
                    label: this.$t('player_selector.tab_friends'),
                    testId: 'player-tab-friends',
                },
            ]
            if (!this.matchStore.isRemotePlay) {
                tabs.push({
                    id: 'guest',
                    label: this.$t('player_selector.tab_guest'),
                    testId: 'player-tab-guest',
                })
            }
            return tabs
        },

        /** Match profiles keyed by friend/profile id, to look up ready-state per friend. */
        matchProfilesByFriendId() {
            const map = new Map()
            ;(this.matchStore.setup.profiles || []).forEach((profile) => {
                map.set(profile.profile_id || profile.id, profile)
            })
            return map
        },

        /** Current player count vs. the effective cap (see usePlayerValidation). */
        partySize() {
            const count = (this.matchStore.setup.profiles || []).length
            return { count, cap: this.effectiveMaxPlayers }
        },

        onlineFriends() {
            return (this.authStore.friends || []).filter((friendship) =>
                this.isOnline(friendship.friend?.last_seen_at),
            )
        },

        offlineFriends() {
            return (this.authStore.friends || []).filter(
                (friendship) => !this.isOnline(friendship.friend?.last_seen_at),
            )
        },

        filteredFriends() {
            const query = (this.friendSearchQuery || '').trim().toLowerCase()
            if (!query) return []
            return (this.authStore.friends || []).filter((friendship) => {
                const friend = friendship.friend
                if (!friend) return false
                const tag = (friend.tag_name || '').toLowerCase()
                const name = (friend.name || '').toLowerCase()
                return tag.includes(query) || name.includes(query)
            })
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId
        if (
            matchId &&
            this.matchStore.setup.id !== matchId &&
            this.matchStore.match.id !== matchId
        ) {
            try {
                await this.matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('Failed to restore match:', error)
                // Redirect on error
                this.$router.replace({ name: 'games' })
                return
            }
        }

        this.matchStore.setCurrentStep(2)
    },
    methods: {
        /** 'joined' | 'invited' | null - null means not yet added to this match. */
        friendMatchStatus(friendId) {
            const profile = this.matchProfilesByFriendId.get(friendId)
            if (!profile) {
                return null
            }
            return profile.ready ? 'joined' : 'invited'
        },

        /** Same badge copy as PlayerSelectionSection's playerBadge, for consistency. */
        friendStatusBadge(friendId) {
            return this.friendMatchStatus(friendId) === 'joined'
                ? this.$t('player_selector.joined_badge')
                : this.$t('player_selector.invite_sent_badge')
        },

        getBackendImageUrl(imageName) {
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/images/${imageName}`
        },
        checkPlayerLimits() {
            if (this.requiresTeamModeForMorePlayers) {
                this.$toast(this.$t('player_selector.team_mode_required'), 5000, this.getTeamModeRequiredMessage())
                return false
            }

            if (this.maxPlayersReached) {
                this.$toast(this.$t('player_selector.max_players_reached'), 5000, this.getMaxPlayersMessage())
                return false
            }

            return true
        },
        async addGuest() {
            if (!this.guestName.trim()) return

            if (!this.checkPlayerLimits()) return

            this.isUpdating = true
            try {
                await this.matchStore.addGuestToMatch(this.guestName)
                this.guestName = ''
                this.$router.replace({ hash: '' })
            } catch (error) {
                console.error('Failed to add guest:', error)
                this.$toast(
                    this.$t('player_selector.error_add_guest'),
                    5000,
                    error.message || this.$t('player_selector.error_add_guest'),
                )
            } finally {
                this.isUpdating = false
            }
        },

        async sendGameInviteToFriend(friend) {
            if (!this.checkPlayerLimits()) return

            this.loadingFriendId = friend.id
            this.isUpdating = true
            console.log('Sending game invite to friend with ID:', this.loadingFriendId)

            try {
                await this.matchStore.addProfileToMatch(friend.id)
                // Give Vue time to render the loading state
                await this.$nextTick()
            } catch (error) {
                console.error('Failed to send game invite to friend:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.message || this.$t('player_selector.error_send_invite'),
                )
            } finally {
                this.loadingFriendId = null
                this.isUpdating = false
            }
        },
        sendGameInvite() {
            console.log('Send game invite functionality to be implemented')
        },
        scanQRCode() {
            this.shareQrUrl = 'https://www.dartshopper.com/'
            this.shareQr = true
        },

        closeQrModal() {
            this.shareQr = false
            this.shareQrUrl = ''
        },

        closeDrawer() {
            this.$router.replace({ hash: '' })
        },
    },
}
</script>
